<template>
    <div class="tool-block my-2" :class="blockClass">
        <!-- Header row -->
        <div class="flex items-center gap-2 px-3 py-1.5">
            <UIcon :name="statusIcon" class="w-4 h-4 flex-shrink-0" :class="iconClass" />
            <span class="font-medium text-slate-700">{{ toolDisplayName }}</span>
            <span v-if="keyIdentifier" class="text-slate-500 truncate max-w-[180px]">{{ keyIdentifier }}</span>
            <span class="flex-1 min-w-0" />
            <span v-if="isProcessing" class="text-[11px] text-blue-500 whitespace-nowrap">{{ active ? (timing?.startedAt ? 'in progress…' : 'preparing…') : 'not running' }}</span>
            <span v-else-if="hasError" class="text-[11px] text-red-500 font-medium whitespace-nowrap">failed</span>
            <span v-else-if="isSoftFailure" class="text-[11px] text-amber-600 whitespace-nowrap">{{ statusMessage }}</span>
            <span v-else-if="isCompleted && statusMessage" class="text-[11px] text-slate-400 whitespace-nowrap">{{ statusMessage }}</span>
        </div>

        <div v-if="showProgress" class="mx-3 mb-2 space-y-1.5" role="status" aria-live="polite">
            <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-slate-600">
                <span>{{ progressLabel }}</span>
                <span class="tabular-nums" aria-live="off">{{ elapsedSeconds }}s elapsed</span>
            </div>
            <div class="h-1 overflow-hidden rounded-full bg-blue-100" role="progressbar" :aria-label="progressLabel">
                <div class="tool-progress-bar h-full w-1/3 rounded-full bg-blue-500" />
            </div>
            <p v-if="takingLonger" class="text-xs text-slate-500">Taking longer than usual. {{ toolName === 'previewSnippet' ? 'Still generating the example email.' : 'Still working on this step.' }}</p>
            <p v-else-if="timing?.expectedDuration" class="text-xs text-slate-500">Usually takes {{ timing.expectedDuration.lowerSeconds }}–{{ timing.expectedDuration.upperSeconds }} seconds, based on recent runs. This is an estimate.</p>
        </div>

        <!-- Error / soft failure -->
        <div v-if="hasError" class="mx-3 mb-2 px-2.5 py-2 rounded-md bg-red-50 border border-red-100 text-red-600">
            {{ part.errorText }}
        </div>
        <div v-if="isSoftFailure && softFailureDetail" class="mx-3 mb-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-100 text-amber-700 text-[11px]">
            <pre class="whitespace-pre-wrap break-all m-0">{{ softFailureDetail }}</pre>
        </div>

        <div v-if="toolName === 'rawUpsertContacts' && isCompleted" class="mx-3 mb-2 space-y-2">
            <div v-if="rawSkippedContacts.length" class="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-amber-800 max-h-64 overflow-y-auto">
                <div class="font-medium mb-1">Skipped contacts</div>
                <ul class="space-y-2">
                    <li v-for="(contact, index) in rawSkippedContacts" :key="index" class="break-words">
                        <div class="font-medium">{{ contact.email }}</div>
                        <div v-for="reason in contact.reasons" :key="reason">{{ skipReasonLabels[reason] || reason }}</div>
                    </li>
                </ul>
            </div>
            <div v-if="rawUpsertErrors.length" class="rounded-md border border-red-100 bg-red-50 p-2.5 text-red-700">
                <div v-for="(error, index) in rawUpsertErrors" :key="index" class="break-words">{{ error.email }}: {{ error.error }}</div>
            </div>
        </div>

        <CampaignEditChatCampaignPerformance v-if="toolName === 'getCampaignPerformance' && isCompleted && !hasError" :result="partOutput" />

        <!-- Signal config -->
        <div v-if="isSignalTool && (parsedConfig || partInput.config)" class="mx-3 mb-2 space-y-2">
            <template v-if="parsedConfig">
                <div v-for="(q, qIdx) in parsedConfig.queries" :key="qIdx"
                    class="rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5">
                    <div class="flex items-center gap-1.5">
                        <UIcon :name="providerIcon(q.providerId)" class="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                        <span class="font-medium text-slate-700">{{ friendlyProviderName(q.providerId) }}</span>
                    </div>
                    <template v-if="q.providerId === 'job' && q.param">
                        <div v-if="q.param.searchPhrases?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Keywords:</span>
                            <span v-for="kw in q.param.searchPhrases" :key="kw"
                                class="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px]">{{ kw }}</span>
                        </div>
                        <div v-if="q.param.maxDaysSincePosted" class="text-slate-500">
                            Posted within: <span class="text-slate-700">{{ formatDays(q.param.maxDaysSincePosted) }}</span>
                        </div>
                    </template>
                    <template v-if="q.providerId === 'employee' && q.param">
                        <div v-if="q.param.jobTitles?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Titles:</span>
                            <span v-for="(t, i) in q.param.jobTitles" :key="i"
                                class="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px]">{{ t }}</span>
                        </div>
                        <div v-if="q.param.jobSeniorities?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Seniorities:</span>
                            <span v-for="seniority in q.param.jobSeniorities" :key="seniority"
                                class="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px]">{{ seniority }}</span>
                        </div>
                        <div v-if="q.param.jobDepartments?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Departments:</span>
                            <span v-for="department in q.param.jobDepartments" :key="department"
                                class="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px]">{{ department }}</span>
                        </div>
                        <div v-if="q.param.enrich" class="text-slate-500">
                            Needs valid email: <span class="text-slate-700">Yes</span>
                        </div>
                    </template>
                    <template v-if="q.providerId === 'news' && q.param">
                        <div v-if="q.param.searchPhrases?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Search:</span>
                            <span v-for="kw in q.param.searchPhrases" :key="kw"
                                class="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px]">{{ kw }}</span>
                        </div>
                        <div v-if="q.param.categories?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Categories:</span>
                            <span v-for="cat in q.param.categories" :key="cat"
                                class="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px]">{{ cat }}</span>
                        </div>
                    </template>
                    <template v-if="q.providerId === 'company-page' && q.param">
                        <div v-if="q.param.pages?.length" class="flex flex-wrap gap-1">
                            <span class="text-slate-500 mr-0.5">Pages:</span>
                            <span v-for="p in q.param.pages" :key="p"
                                class="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px]">{{ p }}</span>
                        </div>
                    </template>
                </div>
                <div v-if="parsedConfig.question" class="space-y-1">
                    <div class="flex items-center gap-1.5">
                        <UIcon name="i-ion-sparkles" class="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                        <span class="font-medium text-slate-700">AI Research</span>
                        <span v-if="parsedConfig.highlight" class="text-[10px] text-slate-400 ml-auto">
                            {{ parsedConfig.highlight === 'reasoning' ? 'Answer Mode' : 'Quotes Mode' }}
                        </span>
                    </div>
                    <div class="text-slate-600 whitespace-pre-wrap break-words bg-slate-50 rounded-md p-2.5 border border-slate-100 max-h-40 overflow-y-auto leading-relaxed">
                        {{ parsedConfig.question }}
                    </div>
                </div>
            </template>
            <template v-else-if="partInput.config">
                <div class="text-slate-500 font-medium">Configuration</div>
                <pre class="text-slate-700 bg-white rounded-md p-2.5 border border-slate-100 text-[10px] max-h-32 overflow-y-auto whitespace-pre-wrap break-all">{{ partInput.config }}</pre>
            </template>
            <!-- Error details when signal creation/update fails -->
            <div v-if="partOutput.created === false || partOutput.create === false || partOutput.updated === false" class="space-y-1.5">
                <div class="text-amber-600 font-medium">Error Details</div>
                <div v-if="partOutput.reason" class="text-amber-700 bg-amber-50 rounded-md p-2.5 border border-amber-200">
                    <span class="font-medium">Reason:</span> {{ partOutput.reason }}
                </div>
                <pre v-if="partOutput.detail" class="text-amber-800 bg-amber-50 rounded-md p-2.5 border border-amber-200 text-[10px] max-h-48 overflow-y-auto whitespace-pre-wrap break-all">{{ formatErrorDetail(partOutput.detail) }}</pre>
            </div>
        </div>

        <!-- updateColdreachDB details -->
        <div v-if="toolName === 'updateColdreachDB' && parsedQuery" class="mx-3 mb-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-1">
            <div v-if="parsedQuery.size?.length" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Size</span>
                <span class="text-slate-700">{{ parsedQuery.size.join(', ') }}</span>
            </div>
            <div v-if="parsedQuery.location?.length" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Location</span>
                <span class="text-slate-700">{{ parsedQuery.location.join(', ') }}</span>
            </div>
            <div v-if="parsedQuery.industry?.length" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Industry</span>
                <span class="text-slate-700">{{ parsedQuery.industry.join(', ') }}</span>
            </div>
            <div v-if="parsedQuery.keywords?.length" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Keywords</span>
                <span class="text-slate-700">{{ parsedQuery.keywords.join(', ') }}</span>
            </div>
            <div v-if="parsedQuery.funding?.length" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Funding</span>
                <span class="text-slate-700">{{ parsedQuery.funding.join(', ') }}</span>
            </div>
            <div v-if="parsedQuery.founded" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Founded</span>
                <span class="text-slate-700">{{ formatRange(parsedQuery.founded) }}</span>
            </div>
            <div v-if="parsedQuery.revenue" class="flex items-start gap-2">
                <span class="text-slate-400 w-16 flex-shrink-0">Revenue</span>
                <span class="text-slate-700">{{ formatRange(parsedQuery.revenue) }}</span>
            </div>
        </div>

        <!-- updateCampaignSetup details -->
        <div v-if="toolName === 'updateCampaignSetup'" class="mx-3 mb-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-1.5">
            <div v-if="partInput.filter !== null && partInput.filter !== undefined" class="space-y-1">
                <div class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Mandatory Filters</div>
                <div class="text-slate-700">
                    <template v-if="requiredFilterGroups.length > 0">
                        <div v-for="(group, idx) in requiredFilterGroups" :key="idx" class="mb-0.5 last:mb-0">
                            <span class="text-slate-400">Required group {{ idx + 1 }} (any alternative):</span>
                            {{ group.join(' OR ') }}
                        </div>
                        <div class="text-slate-500">All required groups must pass.</div>
                    </template>
                    <span v-else class="text-slate-400">(no mandatory filters)</span>
                </div>
            </div>
            <div v-if="partInput.optional !== null && partInput.optional !== undefined">
                <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Optional: </span>
                <span class="text-slate-700">{{ Array.isArray(partInput.optional) && (partInput.optional as number[]).length > 0 ? (partInput.optional as number[]).join(', ') : '(none)' }}</span>
            </div>
            <div v-if="partInput.contacts !== null && partInput.contacts !== undefined">
                <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Contacts: </span>
                <span class="text-slate-700">{{ Array.isArray(partInput.contacts) && (partInput.contacts as number[]).length > 0 ? (partInput.contacts as number[]).join(', ') : '(none)' }}</span>
            </div>
            <div v-if="partInput.filter === null && partInput.optional === null && partInput.contacts === null" class="text-slate-400 italic">
                No changes specified
            </div>
        </div>

        <!-- updateSequenceStep / createSequenceStep details -->
        <div v-if="(toolName === 'updateSequenceStep' || toolName === 'createSequenceStep') && hasStepContent"
            class="mx-3 mb-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-2">
            <div v-if="stepSubject !== undefined && stepSubject !== null" class="space-y-0.5">
                <div class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Subject</div>
                <div class="text-slate-700">{{ stepSubject || '(empty)' }}</div>
            </div>
            <div v-if="stepBody !== undefined && stepBody !== null" class="space-y-0.5">
                <div class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Body</div>
                <div class="text-slate-700 bg-slate-50 rounded px-2.5 py-2 border border-slate-100 max-h-40 overflow-y-auto prose prose-sm"
                    v-html="stepBody || '(empty)'" @click="handlePreviewClick">
                </div>
            </div>
            <div v-if="stepDelay !== undefined && stepDelay !== null">
                <span class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Delay: </span>
                <span class="text-slate-700">{{ stepDelay }} days</span>
            </div>
            <div v-if="!stepSubject && !stepBody && stepDelay === undefined && stepUpdatesRaw" class="space-y-0.5">
                <div class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Updates</div>
                <pre class="text-slate-700 bg-slate-50 rounded p-2.5 border border-slate-100 text-[10px] overflow-x-auto">{{ stepUpdatesRaw }}</pre>
            </div>
        </div>

        <!-- updateSnippet / createSnippet details -->
        <div v-if="(toolName === 'updateSnippet' || toolName === 'createSnippet') && partInput.prompt"
            class="mx-3 mb-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-1">
            <div class="text-slate-400 text-[10px] uppercase tracking-wider font-medium">Prompt</div>
            <div class="text-slate-700 whitespace-pre-wrap bg-slate-50 rounded px-2.5 py-2 border border-slate-100 max-h-40 overflow-y-auto">
                {{ partInput.prompt }}
            </div>
        </div>

        <!-- previewSnippet output -->
        <div v-if="toolName === 'previewSnippet' && isCompleted && partOutput.generatedValue"
            class="mx-3 mb-2 rounded-md border border-slate-200 bg-white p-2.5 space-y-2">
            <div v-if="snippetPreviewContext" class="flex items-center gap-2 text-[11px] text-slate-400">
                <span v-if="snippetPreviewContext.contactName">{{ snippetPreviewContext.contactName }}</span>
                <span v-if="snippetPreviewContext.contactName && snippetPreviewContext.jobTitle">·</span>
                <span v-if="snippetPreviewContext.jobTitle">{{ snippetPreviewContext.jobTitle }}</span>
                <span v-if="(snippetPreviewContext.contactName || snippetPreviewContext.jobTitle) && snippetPreviewContext.companyName">@</span>
                <span v-if="snippetPreviewContext.companyName">{{ snippetPreviewContext.companyName }}</span>
                <span v-if="snippetPreviewContext.signalsUsed" class="ml-auto">{{ snippetPreviewContext.signalsUsed }} signal{{ snippetPreviewContext.signalsUsed !== 1 ? 's' : '' }}</span>
            </div>
            <div class="text-slate-700 whitespace-pre-wrap bg-slate-50 rounded px-2.5 py-2 border border-slate-100 max-h-48 overflow-y-auto leading-relaxed">
                {{ partOutput.generatedValue }}
            </div>
        </div>

        <!-- Processing input summary (when no richer detail exists) -->
        <div v-if="isProcessing && inputSummary && !hasExpandedContent" class="px-3 pb-2 text-slate-500">
            {{ inputSummary }}
        </div>
    </div>
</template>

<script setup lang="ts">
// Tool display name mappings
const toolDisplayNames: Record<string, string> = {
    rawUpsertContacts: 'Add contacts',
    'showCampaignPlan': 'Campaign plan',
    'showCampaignModificationPlan': 'Campaign changes',
    'getAllCampaigns': 'Getting all campaigns',
    'getCampaign': 'Getting campaign details',
    'getCampaignCompanyExclusions': 'Reading campaign company blocks',
    'updateCampaignCompanyExclusions': 'Updating campaign company blocks',
    'getCampaignEvaluation': 'Getting Narration evaluation',
    'getTargetingStats': 'Analyzing targeting stats',
    'getCampaignPerformance': 'Analyzing campaign performance',
    'getOutreachPerformance': 'Analyzing outreach performance',
    'getSequenceSetup': 'Getting sequence setup',
    'createCampaign': 'Creating campaign',
    'updateCampaignMeta': 'Updating campaign metadata',
    'updateCampaignSetup': 'Updating campaign setup',
    'updateColdreachDB': 'Updating account filters',
    'createSignal': 'Creating signal',
    'updateSignal': 'Updating signal',
    'testSignal': 'Testing signal',
    'inspectTestResults': 'Checking test results',
    'launchCampaign': 'Launching campaign',
    'createSequenceStep': 'Creating step',
    'updateSequenceStep': 'Updating step',
    'deleteSequence': 'Deleting sequence',
    'deleteSequenceStep': 'Deleting step',
    'createSnippet': 'Creating snippet',
    'updateSnippet': 'Updating email instructions',
    'deleteSnippet': 'Deleting snippet',
    'previewSnippet': 'Generating example email',
    'getPendingContacts': 'Getting pending contacts',
    'waitForPendingContacts': 'Waiting for pending contacts',
    'preLaunchCheck': 'Estimating launch volume',
    'getAllIndustries': 'Getting industries',
    'getProductContext': 'Getting Product Context',
    'proposeProductContext': 'Proposing Product Context',
    'storeProductContext': 'Storing Product Context',
    // Historical chats may still contain the former tool names.
    'getSalesContext': 'Getting Product Context',
    'storeSalesContext': 'Storing Product Context',
    'getWritingStyle': 'Getting Writing Style',
    'previewWritingStyle': 'Previewing Writing Style',
    'countStaleWritingStyleAssignments': 'Counting stale assignments',
    'proposeWritingStyleChange': 'Proposing Writing Style action',
    'getGlobalInstruction': 'Getting global instructions',
    'storeGlobalInstruction': 'Storing global instructions',
    'scrapePage': 'Scraping page',
    'googleSearch': 'Searching Google',
    'showContactReviews': 'Preparing review cards',
    'showCSVUploader': 'CSV Upload',
    'getAvailableMailboxes': 'Getting available mailboxes',
    'updateSequenceMailboxes': 'Updating sequence mailboxes',
    'updateSequenceSchedule': 'Updating sequence schedule',
    'getAvailableLinkedInAccounts': 'Getting LinkedIn accounts',
    'updateSequenceLinkedInAccounts': 'Updating LinkedIn senders',
    'removeSequenceLinkedInAccounts': 'Removing LinkedIn senders',
    'updateLinkedInConversationSuppression': 'Updating LinkedIn conversation suppression',
    'loadPreset': 'Loading preset',
};

const signalTools = ['createSignal', 'updateSignal'];

const mutatingTools = [
    'createCampaign', 'updateCampaignMeta', 'updateCampaignSetup', 'updateColdreachDB',
    'createSignal', 'updateSignal', 'testSignal', 'launchCampaign',
    'createSequenceStep', 'updateSequenceStep', 'deleteSequence', 'deleteSequenceStep',
    'createSnippet', 'updateSnippet', 'deleteSnippet', 'previewSnippet', 'storeProductContext', 'storeSalesContext', 'storeGlobalInstruction',
    'updateSequenceMailboxes', 'updateSequenceSchedule', 'updateSequenceLinkedInAccounts', 'updateLinkedInConversationSuppression',
];

const props = defineProps<{
    active?: boolean;
    metadata?: unknown;
    part: {
        type: string;
        toolCallId?: string;
        state?: string;
        input?: unknown;
        output?: unknown;
        errorText?: string;
        [key: string]: unknown;
    };
}>();

// Type helpers to safely access input/output
const partInput = computed(() => (props.part.input || {}) as Record<string, unknown>);
const partOutput = computed(() => (props.part.output || {}) as Record<string, unknown>);

const toolName = computed(() => props.part.type.replace('tool-', ''));
const toolDisplayName = computed(() => toolDisplayNames[toolName.value] || toolName.value);
const isSignalTool = computed(() => signalTools.includes(toolName.value));

const rawSkippedContacts = computed(() => (partOutput.value.skippedContacts ?? []) as { email: string; reasons: string[] }[]);
const rawUpsertErrors = computed(() => (partOutput.value.errors ?? []) as { email: string; error: string }[]);
const skipReasonLabels: Record<string, string> = {
    cooldown_same_sequence: 'Already active in this campaign or still in its cooldown period.',
    cooldown_other_sequences: 'Already active in another campaign or still in its cooldown period.',
    unapproved: 'Already has an assignment awaiting approval.',
    replied: 'This contact has already replied.',
    blocked: 'This contact is blocked or unsubscribed.',
    globally_excluded: 'This contact or company is excluded from outreach.',
    campaign_excluded: 'This company is excluded from this campaign only.',
    'provider_incompatible:missing_email': 'An email address is required.',
    'provider_incompatible:missing_linkedin_url': 'A LinkedIn profile URL is required.',
    'provider_incompatible:personal_email_not_allowed': 'Personal email addresses, including Gmail, are not supported by this campaign’s sending provider.',
    'provider_incompatible:missing_first_name': 'A first name is required by the sending provider.',
    'provider_incompatible:failed_email_verification': 'This email address previously bounced.',
    duplicate_in_request: 'This email address appears more than once in this request.',
};

// State detection
const isProcessing = computed(() => {
    if (props.part.output !== undefined) return false;
    if (props.part.errorText) return false;
    return props.part.state === 'input-streaming' ||
        props.part.state === 'input-available' ||
        props.part.state === undefined;
});
// Tool input streaming is model preparation, not tool execution. Server timestamps
// begin when the tool is accepted (including any queue/guard wait) and survive reloads.
const timing = computed(() => {
    const metadata = props.metadata as {
        toolProgress?: Record<string, { startedAt?: number; finishedAt?: number; expectedDuration?: { lowerSeconds: number; upperSeconds: number } }>;
        toolJournal?: Record<string, { startedAt?: number; finishedAt?: number; expectedDuration?: { lowerSeconds: number; upperSeconds: number } }>;
    } | undefined;
    const id = props.part.toolCallId;
    return id ? metadata?.toolProgress?.[id] ?? metadata?.toolJournal?.[id] : undefined;
});
const now = ref(Date.now());
const preparationStartedAt = ref(Date.now());
let progressTimer: ReturnType<typeof setInterval> | undefined;
watch(() => props.active && isProcessing.value && !timing.value?.finishedAt, running => {
    if (progressTimer) clearInterval(progressTimer);
    progressTimer = undefined;
    if (!running) return;
    now.value = Date.now();
    preparationStartedAt.value = now.value;
    progressTimer = setInterval(() => { now.value = Date.now(); }, 250);
}, { immediate: true });
onUnmounted(() => { if (progressTimer) clearInterval(progressTimer); });
const elapsedSeconds = computed(() => Math.max(0, Math.floor((now.value - (timing.value?.startedAt ?? preparationStartedAt.value)) / 1000)));
const showProgress = computed(() => props.active && isProcessing.value && !timing.value?.finishedAt && elapsedSeconds.value >= 1);
const takingLonger = computed(() => timing.value?.expectedDuration && elapsedSeconds.value > timing.value.expectedDuration.upperSeconds);
const progressLabel = computed(() => {
    if (!timing.value?.startedAt) return toolName.value === 'previewSnippet' ? 'Preparing the example request…' : 'Preparing this step…';
    if (toolName.value === 'previewSnippet') return 'Generating an example email to check the instructions…';
    if (toolName.value === 'updateSnippet') return 'Saving email instructions and requesting background refresh…';
    return `${toolDisplayName.value}…`;
});
const isCompleted = computed(() =>
    props.part.state === 'output-available' ||
    (props.part.output !== undefined && !props.part.errorText)
);
const hasError = computed(() =>
    props.part.state === 'output-error' ||
    (props.part.errorText !== undefined && props.part.errorText !== '')
);
const isMutating = computed(() => mutatingTools.includes(toolName.value));

// Detect "soft failure"
const isSoftFailure = computed(() => {
    if (!isCompleted.value) return false;
    const output = partOutput.value;
    if (toolName.value === 'rawUpsertContacts') return Number(output.skipped) > 0 || rawUpsertErrors.value.length > 0;
    if (output.partial === true || output.outcome === 'blocked') return true;
    if (output.error != null) return true;
    if (output.created === false) return true;
    if (output.updated === false && output.unchanged !== true && output.outcome !== 'no_change') return true;
    if (output.withdrawn === false) return true;
    if (output.excluded === false || (output.toggled === false && output.outcome !== 'no_change')) return true;
    if (output.deleted === false) return true;
    if (output.launched === false) return true;
    if (output.success === false) return true;
    if (toolName.value === 'waitForPendingContacts' && output.timedOut === true) return true;
    return false;
});

const softFailureDetail = computed(() => {
    if (!isSoftFailure.value) return null;
    const output = partOutput.value;
    if (output.detail) return formatErrorDetail(output.detail);
    if (output.error && typeof output.error === 'string') return output.error;
    if (typeof output.message === 'string') return output.message;
    if (typeof output.reason === 'string') return output.reason;
    return null;
});

// Snippet preview context
const snippetPreviewContext = computed(() => {
    if (toolName.value !== 'previewSnippet') return null;
    const ctx = partOutput.value.context as Record<string, unknown> | undefined;
    if (!ctx) return null;
    return {
        companyName: ctx.companyName as string | undefined,
        contactName: ctx.contactName as string | undefined,
        jobTitle: ctx.jobTitle as string | undefined,
        signalsUsed: ctx.signalsUsed as number | undefined,
    };
});

// Whether this tool has expanded content
const hasExpandedContent = computed(() => {
    if (isSignalTool.value && (parsedConfig.value || partInput.value.config)) return true;
    if (toolName.value === 'updateColdreachDB' && parsedQuery.value) return true;
    if (toolName.value === 'updateCampaignSetup') return true;
    if ((toolName.value === 'updateSequenceStep' || toolName.value === 'createSequenceStep') && hasStepContent.value) return true;
    if ((toolName.value === 'updateSnippet' || toolName.value === 'createSnippet') && partInput.value.prompt) return true;
    if (toolName.value === 'previewSnippet' && isCompleted.value && partOutput.value.generatedValue) return true;
    return false;
});

// Key identifier
const keyIdentifier = computed(() => {
    const input = partInput.value;
    switch (toolName.value) {
        case 'updateSignal':
        case 'createSignal':
            return input.name ? `"${input.name}"` : '';
        case 'updateColdreachDB':
        case 'updateCampaignSetup':
            return input.icpId ? `#${input.icpId}` : '';
        case 'updateSequenceStep':
        case 'createSequenceStep': {
            const stepNum = ((input.stepIndex as number) || 0) + 1;
            const variantNum = input.variantIndex !== undefined ? `.${(input.variantIndex as number) + 1}` : '';
            return `Step ${stepNum}${variantNum}`;
        }
        case 'updateSnippet':
        case 'createSnippet':
            return input.key ? `{{${input.key}}}` : '';
        case 'loadPreset':
            return input.preset ? `"${String(input.preset)}"` : '';
        default:
            return '';
    }
});

const statusIcon = computed(() => {
    if (hasError.value) return 'i-heroicons-x-circle';
    if (isSoftFailure.value) return 'i-heroicons-exclamation-triangle';
    if (isProcessing.value) return 'i-heroicons-arrow-path';
    if (isCompleted.value) return 'i-heroicons-check-circle';
    return 'i-heroicons-cog-6-tooth';
});

const iconClass = computed(() => {
    if (hasError.value) return 'text-red-500';
    if (isSoftFailure.value) return 'text-amber-500';
    if (isProcessing.value && props.active && !timing.value?.finishedAt) return 'text-blue-500 animate-spin';
    if (isCompleted.value) return isMutating.value ? 'text-green-500' : 'text-slate-400';
    return 'text-slate-400';
});

// Block styling based on state
const blockClass = computed(() => {
    if (hasError.value) return 'tool-block--error';
    if (isSoftFailure.value) return 'tool-block--warn';
    if (isProcessing.value && props.active && !timing.value?.finishedAt) return 'tool-block--active';
    if (isMutating.value && isCompleted.value) return 'tool-block--success';
    return '';
});

// Input summary for processing state
const inputSummary = computed(() => {
    const input = partInput.value;
    switch (toolName.value) {
        case 'getCampaign':
        case 'getTargetingStats':
        case 'getOutreachPerformance':
        case 'getSequenceSetup':
            return input.icpId ? `Campaign #${input.icpId}` : '';
        case 'getCampaignPerformance':
            return input.campaignId ? `Campaign #${input.campaignId}` : '';
        case 'getCampaignEvaluation':
            return input.campaignId ? `Campaign #${input.campaignId}` : '';
        case 'updateCampaignSetup':
            return input.icpId ? `Updating campaign #${input.icpId}` : '';
        case 'updateColdreachDB':
            return input.icpId ? `Updating filters for campaign #${input.icpId}` : '';
        case 'createSignal':
            return input.name ? `Creating "${input.name}"` : '';
        case 'updateSignal':
            return input.name ? `Updating "${input.name}"` : '';
        case 'launchCampaign':
            return input.unsequencedAccountTargetCount == null
                ? 'Target: all accounts'
                : `Target: ${input.unsequencedAccountTargetCount} accounts`;
        case 'createSequenceStep':
        case 'updateSequenceStep':
        case 'deleteSequenceStep':
            return input.stepIndex !== undefined ? `Step ${(input.stepIndex as number) + 1}` : '';
        case 'scrapePage':
            return input.url ? `URL: ${input.url}` : '';
        default:
            return '';
    }
});

// Old saved chats retain their array input; current calls name required groups explicitly.
const requiredFilterGroups = computed(() => {
    const filter = partInput.value.filter;
    const groups = Array.isArray(filter) ? filter
        : filter && typeof filter === 'object' && 'requiredGroups' in filter && Array.isArray(filter.requiredGroups)
            ? filter.requiredGroups.map((group: unknown) => group && typeof group === 'object' && 'alternatives' in group ? group.alternatives : [])
            : [];
    return groups.map((group: unknown) => Array.isArray(group) ? group.map((leaf: unknown) => {
        if (typeof leaf === 'number') return `Signal ${leaf} qualified`;
        if (leaf && typeof leaf === 'object' && 'itemId' in leaf && 'value' in leaf) return `Signal ${leaf.itemId} ${leaf.value}`;
        return 'Unknown condition';
    }) : []);
});

// Status message for completed state
const statusMessage = computed(() => {
    const output = partOutput.value;
    const input = partInput.value;

    if (toolName.value === 'updateCampaignCompanyExclusions' && (output.withdrawal as { status?: string } | undefined)?.status === 'failed') return 'Rules saved · removal request failed';
    if (isSoftFailure.value && toolName.value !== 'rawUpsertContacts') return output.partial ? 'Partially applied' : 'Not applied';
    if (output.deliveryStatus === 'deferred') return 'Report generated · delivery separate';
    switch (toolName.value) {
        case 'updateCampaignCompanyExclusions': {
            const withdrawal = output.withdrawal as { status?: string } | undefined;
            return withdrawal?.status === 'failed' ? 'Rules saved · removal request failed'
                : withdrawal?.status === 'requested' ? 'Rules saved · removal requested'
                : 'Company rules saved · assignments not restored';
        }
        case 'getCampaignCompanyExclusions':
            return `${output.remainingAssignmentCount ?? 0} matching assignments remain locally`;
        case 'excludeContactFromCampaign':
            return output.excluded === true ? 'Excluded · withdrawal requested' : 'Not excluded';
        case 'withdrawSupportTicket':
            return output.alreadyWithdrawn ? 'Already withdrawn' : output.withdrawn ? 'Request withdrawn' : 'Not withdrawn';
        case 'approveSelectedContacts':
            return 'Approval requested · completion pending';
        case 'readExportContent':
            return output.nextOffset == null ? 'Export page read' : 'Export page read · more available';
        case 'rawUpsertContacts':
            return `${output.created ?? 0} added · ${output.skipped ?? 0} skipped${rawUpsertErrors.value.length ? ` · ${rawUpsertErrors.value.length} errors` : ''}`;
        case 'getAllCampaigns': {
            const campaigns = (output.campaigns || []) as unknown[];
            return `Found ${campaigns.length} campaign${campaigns.length !== 1 ? 's' : ''}`;
        }
        case 'updateCampaignSetup':
            if (output.success) return 'Updated';
            return output.reason || 'Update failed';

        case 'updateColdreachDB':
            if (output.updated) return 'Updated';
            return output.schemaValid === false ? 'Invalid schema' : 'Update failed';

        case 'createSignal':
            if (output.created || output.create) return `Created (ID: ${output.signalId})`;
            return output.reason || 'Creation failed';

        case 'updateSignal':
            if (output.updated) {
                return output.signalReplaced ? 'Updated (duplicated)' : 'Updated';
            }
            return output.reason || 'Update failed';

        case 'launchCampaign':
            if (output.launched) {
                return output.unsequencedAccountTargetCount == null
                    ? 'Launched (all accounts)'
                    : `Launched (${output.unsequencedAccountTargetCount} accounts)`;
            }
            if (output.reason === 'unsequenced-account-target-limit') return 'Organization account limit exceeded';
            return output.reason || 'Launch failed';

        case 'createSequenceStep':
            if (output.created) return `Step ${((input.stepIndex as number) || 0) + 1} created`;
            return output.error || 'Creation failed';

        case 'updateSequenceStep':
            if (output.updated) return `Step ${((input.stepIndex as number) || 0) + 1} updated`;
            return output.error || 'Update failed';

        case 'deleteSequence':
            if (output.deleted) return 'Deleted';
            return output.error || 'Deletion failed';

        case 'deleteSequenceStep':
            if (output.deleted) {
                return input.variantIndex !== undefined
                    ? `Variant deleted from step ${((input.stepIndex as number) || 0) + 1}`
                    : `Step ${((input.stepIndex as number) || 0) + 1} deleted`;
            }
            return output.error || 'Deletion failed';

        case 'createSnippet':
            if (output.created) return `{{${output.key}}} created`;
            return output.error || 'Creation failed';

        case 'updateSnippet':
            if (output.unchanged === true) return 'Unchanged';
            if (output.updated) return 'Updated';
            return output.error || 'Update failed';

        case 'deleteSnippet':
            if (output.deleted) return 'Deleted';
            return output.error || 'Deletion failed';

        case 'testSignal':
            if (output.started) return 'Test started';
            return output.skipReason || 'Skipped';

        case 'storeProductContext':
        case 'storeSalesContext':
        case 'storeGlobalInstruction':
            if (output.updated) return 'Saved';
            return 'Failed';

        case 'createCampaign':
            if (output.campaignId) return `Created (#${output.campaignId})`;
            return 'Creation failed';

        case 'updateCampaignMeta':
            return 'Updated';

        case 'getCampaign':
            return output.name ? `"${output.name}"` : 'Loaded';

        case 'getCampaignEvaluation': {
            const evaluation = output.evaluation as { status?: string } | null | undefined;
            if (!evaluation) return 'Narration evaluation not ready yet';
            if (evaluation.status === 'completed') return 'Narration evaluation loaded';
            if (evaluation.status === 'failed') return 'Narration evaluation failed';
            if (evaluation.status) return `Narration evaluation ${evaluation.status}`;
            return 'Narration evaluation loaded';
        }

        case 'getTargetingStats':
            return 'Loaded';

        case 'getSequenceSetup': {
            const seqCount = Array.isArray(output) ? output.length : 0;
            return `${seqCount} sequence${seqCount !== 1 ? 's' : ''}`;
        }

        case 'getCampaignPerformance':
            if (output.error) return 'Unavailable';
            if (output.status === 'no-connected-sequence') return 'No sequence';
            return 'Performance';
        case 'getOutreachPerformance':
            if (output.status === 'no-connected-sequence') return 'No sequence';
            return 'Loaded';

        case 'inspectTestResults': {
            const results = (output.testResults || []) as { signalStatus: string }[];
            const readyCount = results.filter(r => r.signalStatus === 'ready').length;
            return `${readyCount}/${results.length} ready`;
        }

        case 'previewSnippet':
            if (output.error) return output.error;
            return 'Preview ready';

        case 'getPendingContacts':
            if (output.error) return output.error;
            return `${output.previewCount}/${output.totalPendingCount} contacts`;

        case 'waitForPendingContacts':
            if (output.error) return output.error;
            if (output.timedOut) return 'No contacts yet';
            if (output.skipped) return output.message || output.reason || 'Skipped';
            return `${output.previewCount}/${output.totalPendingCount} contacts`;

        case 'preLaunchCheck':
            return 'Pre-launch estimate ready';

        case 'scrapePage':
            if (output.success) return 'Loaded';
            return 'Failed';

        case 'googleSearch': {
            const pageCount = ((output.pages || []) as unknown[]).length;
            return `${pageCount} result${pageCount !== 1 ? 's' : ''}`;
        }

        case 'getAvailableMailboxes': {
            const mailboxCount = ((output.mailboxes || []) as unknown[]).length;
            return `${mailboxCount} mailbox${mailboxCount !== 1 ? 'es' : ''}`;
        }

        case 'updateSequenceMailboxes':
            if (output.updated) return 'Updated';
            return output.message || output.reason || 'Failed';

        case 'updateSequenceSchedule':
            if (output.updated) return 'Updated';
            return output.message || output.reason || 'Failed';

        case 'getAvailableLinkedInAccounts': {
            const accountCount = ((output.accounts || []) as unknown[]).length;
            return `${accountCount} account${accountCount !== 1 ? 's' : ''}`;
        }

        case 'updateSequenceLinkedInAccounts':
            if (output.updated) {
                const assignedCount = ((output.linkedInAccounts || []) as unknown[]).length;
                return `${assignedCount} assigned`;
            }
            return output.message || output.reason || 'Failed';

        case 'removeSequenceLinkedInAccounts':
            return output.status === 'pending' ? 'Cleanup pending' : output.message || output.reason || 'Failed';

        case 'updateLinkedInConversationSuppression':
            if (output.updated) return 'Updated';
            return output.message || output.reason || 'Failed';

        case 'loadPreset':
            if (output.loaded) return `Loaded: ${output.preset}`;
            return output.error || 'Failed';

        default:
            return 'Done';
    }
});

// Parsed config for signal tools
const parsedConfig = computed(() => {
    if (!['updateSignal', 'createSignal'].includes(toolName.value)) return null;
    const configStr = partInput.value.config as string;
    if (!configStr) return null;
    try {
        return JSON.parse(configStr);
    } catch {
        return null;
    }
});

// Parsed query for ColdreachDB
const parsedQuery = computed(() => {
    if (toolName.value !== 'updateColdreachDB') return null;
    const queryStr = partInput.value.query as string;
    if (!queryStr) return null;
    try {
        return JSON.parse(queryStr);
    } catch {
        return null;
    }
});

// Step details
const stepSubject = computed(() => {
    if (toolName.value === 'updateSequenceStep') {
        return (partInput.value.updates as Record<string, unknown>)?.subject as string;
    }
    if (toolName.value === 'createSequenceStep') {
        return (partInput.value.data as Record<string, unknown>)?.subject as string;
    }
    return null;
});

const stepBody = computed(() => {
    if (toolName.value === 'updateSequenceStep') {
        return (partInput.value.updates as Record<string, unknown>)?.body as string;
    }
    if (toolName.value === 'createSequenceStep') {
        return (partInput.value.data as Record<string, unknown>)?.body as string;
    }
    return null;
});

const stepDelay = computed(() => {
    if (toolName.value === 'updateSequenceStep') {
        return (partInput.value.updates as Record<string, unknown>)?.delayInDays as number | undefined;
    }
    if (toolName.value === 'createSequenceStep') {
        return (partInput.value.data as Record<string, unknown>)?.delayInDays as number | undefined;
    }
    return undefined;
});

const hasStepContent = computed(() =>
    stepSubject.value !== null || stepBody.value !== null || stepDelay.value !== undefined || !!stepUpdatesRaw.value
);

const stepUpdatesRaw = computed(() => {
    if (toolName.value === 'updateSequenceStep') {
        const updates = partInput.value.updates as Record<string, unknown>;
        if (updates && Object.keys(updates).length > 0) {
            return JSON.stringify(updates, null, 2);
        }
    }
    if (toolName.value === 'createSequenceStep') {
        const data = partInput.value.data as Record<string, unknown>;
        if (data && Object.keys(data).length > 0) {
            return JSON.stringify(data, null, 2);
        }
    }
    return null;
});

const formatRange = (range: [number | null, number | null] | undefined) => {
    if (!range) return '';
    const [min, max] = range;
    if (min !== null && max !== null) return `${min} - ${max}`;
    if (min !== null) return `>= ${min}`;
    if (max !== null) return `<= ${max}`;
    return '';
};

const formatErrorDetail = (detail: unknown): string => {
    if (!detail) return '';
    if (typeof detail === 'string') return detail;
    if (typeof detail === 'object') {
        const zodError = detail as { issues?: Array<{ path: (string | number)[]; message: string; code?: string }> };
        if (Array.isArray(zodError.issues)) {
            return zodError.issues
                .map(issue => {
                    const path = issue.path.length > 0 ? `[${issue.path.join('.')}] ` : '';
                    return `${path}${issue.message}`;
                })
                .join('\n');
        }
        return JSON.stringify(detail, null, 2);
    }
    return String(detail);
};

const providerNameMap: Record<string, string> = {
    'job': 'Hiring Signals',
    'company-page': 'Website Checks',
    'employee': 'Contacts',
    '10k': '10-K Report',
    'news': 'News',
    'company-info': 'Company Info',
    'uploaded-contacts': 'Uploaded Contacts',
    'company-linkedin-profile': 'Company LinkedIn',
};

const providerIconMap: Record<string, string> = {
    'job': 'i-heroicons-briefcase',
    'company-page': 'i-heroicons-globe-alt',
    'employee': 'i-heroicons-users',
    '10k': 'i-heroicons-document-chart-bar',
    'news': 'i-heroicons-newspaper',
    'company-info': 'i-heroicons-building-office',
    'uploaded-contacts': 'i-heroicons-arrow-up-tray',
    'company-linkedin-profile': 'i-heroicons-link',
};

const friendlyProviderName = (providerId: string) => providerNameMap[providerId] || providerId;
const providerIcon = (providerId: string) => providerIconMap[providerId] || 'i-heroicons-cube';

const formatDays = (days: number): string => {
    if (days <= 0) return 'Any Time';
    if (days <= 14) return 'Recent';
    if (days <= 30) return '1 Month';
    if (days <= 90) return '3 Months';
    if (days <= 180) return '6 Months';
    if (days <= 365) return '1 Year';
    return `${Math.round(days / 365)} Years`;
};

function handlePreviewClick(event: MouseEvent) {
    const target = (event.target as HTMLElement).closest('a');
    if (target && target.tagName === 'A') {
        event.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
            window.open(href, '_blank', 'noopener,noreferrer');
        }
    }
}
</script>

<style scoped>
.tool-block {
    border-left: 3px solid rgb(203 213 225);
    border-radius: 6px;
    background: rgb(248 250 252 / 0.6);
    font-size: 12px;
}

.tool-block--error {
    border-left-color: rgb(239 68 68);
    background: rgb(254 242 242 / 0.5);
}

.tool-block--warn {
    border-left-color: rgb(245 158 11);
    background: rgb(255 251 235 / 0.5);
}

.tool-block--active {
    border-left-color: rgb(59 130 246);
    background: rgb(239 246 255 / 0.5);
}

.tool-block--success {
    border-left-color: rgb(34 197 94);
    background: rgb(240 253 244 / 0.4);
}
</style>

<style scoped>
.tool-progress-bar {
    animation: tool-progress 1.6s ease-in-out infinite;
}
@keyframes tool-progress {
    from { transform: translateX(-100%); }
    to { transform: translateX(400%); }
}
@media (prefers-reduced-motion: reduce) {
    .tool-progress-bar { animation: none; width: 100%; opacity: 0.5; }
}
</style>
